import { Download } from 'lucide-react'
import { scriptExportUrl } from '@/api/client'
import type { ExportFormat } from '@/api/types'
import { Button } from '@/admin-design/_common'

export type ExportButtonVariant = 'primary' | 'secondary'

interface Props {
  scriptId: number
  format: ExportFormat
  label: string
  variant?: ExportButtonVariant
}

// ExportButton is a stateless link styled as a button. It points the browser
// at the export endpoint and adds the `download` attribute so the response's
// Content-Disposition: attachment header drives the file save.
//
// We deliberately use <a download> instead of fetch + Blob because:
//   1. The server already sets the correct filename via Content-Disposition.
//   2. Browsers stream the download — no full-buffer decode in the renderer.
//   3. The session cookie travels automatically; no auth shuffle needed.
export default function ExportButton({ scriptId, format, label, variant = 'primary' }: Props) {
  const href = scriptExportUrl(scriptId, format)
  const filename = `script-${scriptId}.${format}` // hint only; server overrides

  return (
    <Button
      as="a"
      href={href}
      download={filename}
      variant={variant}
      size="lg"
      data-testid={`export-${format}`}
    >
      <Download size={16} aria-hidden="true" />
      <span>{label}</span>
    </Button>
  )
}
